import React from "react";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  FormControlLabel,
  Select,
  MenuItem,
  Checkbox,
  FormHelperText,
  Alert,
} from "@mui/material";
import type { TimePeriod } from "@/store/slices/crosstab-config-slice";
import {
  getRollingWindowOptions,
  getRollingAverageHelperText,
} from "@/utils/rolling-average-utils";

interface RollingAverageSectionProps {
  enabled: boolean;
  windowSize: number;
  timePeriod: TimePeriod;
  totalAvailablePeriods?: number;
  onEnabledChange: (enabled: boolean) => void;
  onWindowSizeChange: (size: number) => void;
  disabled?: boolean;
  /**
   * When provided, restricts available window sizes to this list (from dataset guardrails).
   * Excludes 0 (No Rolling Average) — that is handled via guardrailNoRollingAllowed.
   */
  guardrailAllowedWindowSizes?: number[];
  /**
   * When true, "No Rolling Average" is not an allowed option — the checkbox is forced on
   * and cannot be unchecked.
   */
  guardrailNoRollingDisabled?: boolean;
  /**
   * When true, no rolling-average window sizes are allowed by dataset policy — the
   * checkbox is forced off and cannot be checked.
   */
  guardrailRollingDisabled?: boolean;
  /** Inline warning shown when the current config is incompatible with dataset guardrails. */
  guardrailConflictWarning?: string;
}

const RollingAverageSection: React.FC<RollingAverageSectionProps> = ({
  enabled,
  windowSize,
  timePeriod,
  totalAvailablePeriods,
  onEnabledChange,
  onWindowSizeChange,
  disabled = false,
  guardrailAllowedWindowSizes,
  guardrailNoRollingDisabled = false,
  guardrailRollingDisabled = false,
  guardrailConflictWarning,
}) => {
  const isAllTime = timePeriod === "all_time";
  const allWindowOptions = getRollingWindowOptions(timePeriod);

  // Filter window options to guardrail allow-list when provided
  const windowOptions = guardrailAllowedWindowSizes
    ? allWindowOptions.filter((o) =>
        guardrailAllowedWindowSizes.includes(o.value),
      )
    : allWindowOptions;

  // Check if rolling window exceeds available data
  const windowExceedsData =
    enabled &&
    !isAllTime &&
    totalAvailablePeriods !== undefined &&
    windowSize > totalAvailablePeriods;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Rolling Average
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mt: 2,
        }}
      >
        {guardrailConflictWarning && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {guardrailConflictWarning}
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox
              disabled={
                disabled ||
                isAllTime ||
                guardrailNoRollingDisabled ||
                guardrailRollingDisabled
              }
              checked={
                guardrailRollingDisabled
                  ? false
                  : (enabled && !isAllTime) || guardrailNoRollingDisabled
              }
              onChange={(e) => {
                onEnabledChange(e.target.checked);
              }}
              size="small"
            />
          }
          label={
            guardrailNoRollingDisabled
              ? "Apply Rolling Average (required by dataset policy)"
              : "Apply Rolling Average"
          }
        />

        {enabled && !isAllTime && (
          <FormControl fullWidth size="small">
            <FormLabel className="text-[12px] text-[#262626]">
              Rolling Window Size
            </FormLabel>
            <Select
              disabled={disabled}
              value={windowSize}
              onChange={(e) => {
                onWindowSizeChange(Number(e.target.value));
              }}
            >
              {windowOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {getRollingAverageHelperText(windowSize, timePeriod)}
            </FormHelperText>
          </FormControl>
        )}

        {windowExceedsData && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Warning: Selected rolling window ({windowSize} periods) exceeds
            available data. All data points will show partial rolling averages.
          </Alert>
        )}

        {isAllTime ? (
          <Typography variant="caption" color="text.secondary">
            Rolling averages require a specific time period selection.
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Rolling average smooths out data fluctuations to show trends more
            clearly
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RollingAverageSection;
